import type { LstWithdrawalReaderKind } from './types'

/**
 * Static registry entry for an LST's withdrawal-request enumeration
 * surface.
 *
 * Each entry pins: the LST share-token address, the brand/symbol
 * (for output labelling), the reader kind, and any auxiliary contract
 * addresses the reader needs (the withdrawal-manager NFT/queue
 * contract, Polygon stake manager for MaticX, …).
 *
 * Mirrors [LST_WITHDRAWAL_QUEUES.md](../LST_WITHDRAWAL_QUEUES.md);
 * read that for per-protocol semantics.
 */
export interface LstWithdrawalRegistryEntry {
  /** LST share-token address (matches `address` in
   *  `../registry.ts → LST_REGISTRY`). Lowercased. */
  lst: string
  /** Brand label. */
  brand: string
  /** Symbol — used to label the per-asset request list in UI. */
  symbol: string
  /** Reader kind. */
  reader: LstWithdrawalReaderKind
  /** Optional withdrawal contract — separate from the LST share token
   *  for most protocols. Lowercased.
   *  - Lido: WithdrawalQueueERC721
   *  - EtherFi: WithdrawRequestNFT
   *  - YieldNest: WithdrawalQueueManager
   *  - Stader ETHx: UserWithdrawalManager
   *  - Stader MaticX: the home contract itself
   *  - Renzo: WithdrawQueue
   *  - BENQI sAVAX: the sAVAX contract itself
   *  - Beets stS: the SonicStaking proxy itself
   *  - Hyperbeat hbHYPE: the share token itself (ERC-7540 surface) */
  withdrawalContract?: string
  /** Polygon IStakeManager — only for `staderMaticXQueue`. The
   *  finalization check requires `epoch()` + `withdrawalDelay()`. */
  polygonStakeManager?: string
  /** Second escrow contract probed with the same reader — only for
   *  `strataCooldown`, where a market runs both an `UnstakeCooldown`
   *  (base-asset leg, in `withdrawalContract`) and an `ERC20Cooldown`
   *  (collateral-token leg). Lowercased. */
  secondaryWithdrawalContract?: string
  /** The token an escrow's requests are booked under — only for
   *  `strataCooldown` (`balanceOf(escrowToken, user)` /
   *  `finalize(escrowToken, user)`). The market's staked collateral
   *  (sUSDe, sNUSD, mHYPER, …), NOT the tranche's `asset()`.
   *  Lowercased. */
  escrowToken?: string
}

/** Per-chain LST withdrawal-reader allowlist. */
export const LST_WITHDRAWAL_REGISTRY: {
  [chainId: string]: LstWithdrawalRegistryEntry[]
} = {
  '1': [
    {
      // Treehouse tETH — `TreehouseRedemptionV2`, the queued (5 bps / 7 d)
      // exit. The instant Fastlane leg settles in the same transaction and
      // so never produces a pending request. Undocumented address,
      // recovered from `Vault.redemption()` →
      // `RedemptionController.getRedemptionContracts()`.
      lst: '0xd11c452fc99cf405034ee446803b6f6c1f6d5ed8',
      brand: 'Treehouse',
      symbol: 'tETH',
      reader: 'treehouseRedemptionQueue',
      withdrawalContract: '0xcd63a29faff07130d3af89bb4f40778938aabb85',
    },
    {
      // wBETH — Binance's UnwrapTokenV1 queue, the SAME contract address
      // on Ethereum and BNB. Entered via `wBETH.requestWithdrawEth`;
      // `getUserWithdrawRequests(user)` enumerates open requests, and
      // `claimWithdraw(index)` takes the user's ARRAY POSITION (swap-and-pop,
      // so ids shift on every claim — never cache them). `lockTime()` is
      // 10 days today but is admin-settable down to 2, and the ETH owed is
      // frozen at request time, so the position stops earning meanwhile.
      lst: '0xa2e3356610840701bdf5611a53974510ae27e2e1',
      brand: 'Binance',
      symbol: 'wBETH',
      reader: 'binanceWbethQueue',
      withdrawalContract: '0x79973d557cd9dd87eb61e250cc2572c990e20196',
    },
    // Ankr ankrETH — Ankr unstake queue; reader not yet implemented.
    {
      lst: '0xe95a203b1a91a908f9b9ce46459d101078c2c3cb',
      brand: 'Ankr',
      symbol: 'ankrETH',
      reader: 'unverified',
    },
    // BTC LSTs — exits via issuer/DEX or a protocol queue; per-protocol
    // readers not yet implemented. Listed as `unverified` placeholders so the
    // orchestrator surfaces the asset (returns [] requests) instead of
    // silently omitting it.
    {
      lst: '0x8db2350d78abc13f5673a411d4700bcf87864dde',
      brand: 'Swell',
      symbol: 'swBTC',
      reader: 'unverified',
    },
    {
      lst: '0x8236a87084f8b84306f72007f36f2618a5634494',
      brand: 'Lombard',
      symbol: 'LBTC',
      reader: 'unverified',
    },
    {
      // Lombard LBTCv (Veda BoringVault) — queue mechanics not yet
      // implemented; placeholder so the orchestrator surfaces the asset.
      lst: '0x5401b8620e5fb570064ca9114fd1e135fd77d57c',
      brand: 'Lombard',
      symbol: 'LBTCv',
      reader: 'unverified',
    },
    {
      lst: '0x657e8c867d8b37dcc18fa4caead9c45eb088c642',
      brand: 'ether.fi',
      symbol: 'eBTC',
      reader: 'unverified',
    },
    {
      lst: '0x004e9c3ef86bc1ca1f0bb5c7662861ee93350568',
      brand: 'Bedrock',
      symbol: 'uniBTC',
      reader: 'unverified',
    },
    {
      lst: '0x2ec37d45fcae65d9787ecf71dc85a444968f6646',
      brand: 'Bedrock',
      symbol: 'brBTC',
      reader: 'unverified',
    },
    {
      lst: '0xf469fbd2abcd6b9de8e169d128226c0fc90a012e',
      brand: 'PumpBTC',
      symbol: 'pumpBTC',
      reader: 'unverified',
    },
    {
      lst: '0x7a56e1c57c7475ccf742a1832b028f0456652f97',
      brand: 'Solv',
      symbol: 'SolvBTC',
      reader: 'unverified',
    },
    {
      lst: '0xd9d920aa40f578ab794426f5c90f6c731d159def',
      brand: 'Solv',
      symbol: 'xSolvBTC',
      reader: 'unverified',
    },
    {
      lst: '0xcea2daf93617b97504e05affc5bcf9b3922d3034',
      brand: 'Solv',
      symbol: 'BTC+',
      reader: 'unverified',
    },
    {
      lst: '0x7f39c581f595b53c5cb19bd0b3f8da6c935e2ca0',
      brand: 'Lido',
      symbol: 'wstETH',
      reader: 'lidoQueue',
      withdrawalContract: '0x889edc2edab5f40e902b864ad4d7ade8e412f9b1',
    },
    {
      // sUSDe (Ethena savings) — cooldowns(address) escrow. Cooldown
      // duration is governance-mutable; when set to 0 the vault is a
      // vanilla 4626 and this reader returns [] (empty cooldown slot).
      lst: '0x9d39a5de30e57443bff2a8307a4256c8797a3497',
      brand: 'Ethena',
      symbol: 'sUSDe',
      reader: 'ethenaCooldown',
    },
    {
      // Avant savETH — StakedUSDeV2-style cooldown (avETH is Ethereum-native).
      // Verified on-chain: cooldownDuration()=86400, cooldowns(address) getter.
      lst: '0xda06ee2dacf9245aa80072a4407debdea0d7e341',
      brand: 'Avant',
      symbol: 'savETH',
      reader: 'ethenaCooldown',
    },
    {
      // Tori strUSD — StakedUSDeV2 clone over trUSD; same cooldowns(address)
      // escrow surface (7d, owner-mutable), claim via `unstake`.
      lst: '0x280839980a7ed0d7717f64125fe241012e5f5815',
      brand: 'Tori',
      symbol: 'strUSD',
      reader: 'ethenaCooldown',
    },
    {
      // Neutrl sNUSD — StakedUSDeV2 clone over NUSD; cooldowns(address)
      // escrow (10d), claim via `unstake`.
      lst: '0x08efcc2f3e61185d0ea7f8830b3fec9bfa2ee313',
      brand: 'Neutrl',
      symbol: 'sNUSD',
      reader: 'ethenaCooldown',
    },
    {
      // Brix wiTRY — StakedUSDeV2 clone over iTRY (Turkish lira); same
      // cooldowns(address) escrow, claim via `unstake`. The duration is
      // owner-settable with no timelock (documented 3 d, live 300 s on
      // 2026-08-14, 90 d ceiling), which is exactly why the pending exit is
      // read per-user from `cooldowns()` rather than derived from a constant.
      lst: '0xe346c29b5b60ef870b9724c57ccfbbc631e47dee',
      brand: 'Brix',
      symbol: 'wiTRY',
      reader: 'ethenaCooldown',
    },
    {
      // 3Jane sUSD3 — startCooldown(shares) → 30d → plain 4626 redeem
      // inside the withdrawal window; getCooldownStatus(address) getter.
      lst: '0xf689555121e529ff0463e191f9bd9d1e496164a7',
      brand: '3Jane',
      symbol: 'sUSD3',
      reader: 'susd3Cooldown',
    },
    {
      // Apyx apyUSD — NOT enumerable today. The 4626 redeem escrows the
      // apxUSD and mints a **soulbound ERC-721** on the receipt contract
      // `apyUSD.receipt()` = 0x9bf51f33…, which is the only handle on the
      // pending exit and exposes no per-owner enumeration. The vault also
      // has an `unlockToken()` (0x93775e2d…) carrying 7540-shaped getters,
      // but they are the wrong surface: verified 2026-08-04 that
      // `pendingRedeemRequest(0, user)` / `claimableRedeemRequest(0, user)`
      // both return 0 for addresses genuinely holding open receipt NFTs.
      // The claim needs the `tokenId` (read from the redeem's `Transfer`
      // log), so the real fix is a caller-supplied-ids reader in the
      // Mantle/Puffer/TruFin mould — see INDEXING_STRATEGIES.md.
      lst: '0x38eeb52f0771140d10c4e9a9a72349a329fe8a6a',
      brand: 'Apyx',
      symbol: 'apyUSD',
      reader: 'unverified',
    },
    // Strata tranche markets — ONE entry per market (not per tranche):
    // both tranches escrow into the same two per-market ICooldown
    // contracts keyed by (escrowToken, user) with no tranche
    // attribution, so per-tranche rows would double-report. `lst` is the
    // SENIOR tranche address — the catalog resolves it to the savings
    // provider, and the calldata-sdk `strata` claim built against it
    // finalizes the whole market's unlocked requests (senior AND
    // junior). The escrow is KEYED by the collateral token but its
    // recorded amounts are per-leg — see the reader for the trap.
    {
      lst: '0x3d7d6fdf07ee548b939a80edbc9b2256d0cdc003', // srUSDe
      brand: 'Strata',
      symbol: 'srUSDe/jrUSDe',
      reader: 'strataCooldown',
      withdrawalContract: '0x735eddf50ca2371aa48466469c742e684c610f74', // UnstakeCooldown
      secondaryWithdrawalContract: '0xd6dad17d025cddded27305aebab8b277996a6faf', // ERC20Cooldown
      escrowToken: '0x9d39a5de30e57443bff2a8307a4256c8797a3497', // sUSDe
    },
    {
      lst: '0x65a44528e8868166401ea08b549e19552af589db', // srNUSD
      brand: 'Strata',
      symbol: 'srNUSD/jrNUSD',
      reader: 'strataCooldown',
      withdrawalContract: '0x2a52363a2a0d765b31cb117a8e4d9ce58c2bc749', // NeutrlUnstakeCooldown
      secondaryWithdrawalContract: '0x1abc3c3c15a862276d057b2acbcfeab358907990', // NeutrlERC20Cooldown
      escrowToken: '0x08efcc2f3e61185d0ea7f8830b3fec9bfa2ee313', // sNUSD
    },
    {
      lst: '0x627ea69929212916ec57b1b26d2e1a19f6129b53', // srmHYPER
      brand: 'Strata',
      symbol: 'srmHYPER/jrmHYPER',
      reader: 'strataCooldown',
      withdrawalContract: '0x7910cb19a4f5a36caa2bed0af82cbfff2e08805c', // MHyperUnstakeCooldown
      secondaryWithdrawalContract: '0x71ec07bba60e854c4b2466068be1c8d5b5788fda', // MHyperERC20Cooldown
      escrowToken: '0x9b5528528656dbc094765e2abb79f293c21191b9', // mHYPER
    },
    {
      lst: '0xcced21d609cac4a272d0c01a8ff4de9cebc40d60', // srmM1-USD
      brand: 'Strata',
      symbol: 'srmM1-USD/jrmM1-USD',
      reader: 'strataCooldown',
      withdrawalContract: '0x6f6904ed406cefb34893054a742c6148e9d681c1', // MM1USDUnstakeCooldown
      secondaryWithdrawalContract: '0x4c2680ae06a0bb0b091e5ccca13cb03f4f72296e', // MM1USDERC20Cooldown
      escrowToken: '0xcc5c22c7a6bcc25e66726aef011dde74289ed203', // mM1-USD
    },
    {
      lst: '0xfaa9a0e1db9e22ae3a20b2b58a68dc24d053d066', // srUSDat
      brand: 'Strata',
      symbol: 'srUSDat/jrUSDat',
      reader: 'strataCooldown',
      withdrawalContract: '0x616af703d5739f2122af48c0d1ce0b37e09381ac', // SaturnUnstakeCooldown
      secondaryWithdrawalContract: '0x25138d10837ab0278eb860454e1045f2b92929ab', // SaturnERC20Cooldown
      escrowToken: '0xd166337499e176bbc38a1fbd113ab144e5bd2df7', // sUSDat
    },
    {
      lst: '0x35bff778d3fc53a561486bf28e761428499232eb', // srPRIME
      brand: 'Strata',
      symbol: 'srPRIME/jrPRIME',
      reader: 'strataCooldown',
      withdrawalContract: '0x440938fcb03d64afe50a8168d4b3cfcc341bfbaa', // FigureUnstakeCooldown
      secondaryWithdrawalContract: '0xbe71d21addba5c9aac5b4e006db1715ef1e324be', // FigureERC20Cooldown
      escrowToken: '0x19ebb35279a16207ec4ba82799cc64715065f7f6', // PRIME
    },
    {
      lst: '0xcd5fe23c85820f7b72d0926fc9b05b43e359b7ee',
      brand: 'EtherFi',
      symbol: 'weETH',
      reader: 'etherfiNft',
      withdrawalContract: '0x7d5706f6ef3f89b3951e23e557cdfbc3239d4e2c',
    },
    {
      lst: '0x09db87a538bd693e9d08544577d5ccfaa6373a48',
      brand: 'YieldNest',
      symbol: 'ynETH',
      reader: 'yieldNestNft',
      withdrawalContract: '0x0bc9bc81ad379810b36ad5cc95387112990aa67b',
    },
    {
      // ynLSDe shares the ynETH WithdrawalQueueManager interface
      // (ERC-721 claim NFTs + secondsToFinalization). Its own manager —
      // `redeemableAsset()` == ynLSDe, verified on-chain.
      lst: '0x35ec69a77b79c255e5d47d5a3bdbefefe342630c',
      brand: 'YieldNest',
      symbol: 'ynLSDe',
      reader: 'yieldNestNft',
      withdrawalContract: '0x8face3283e20b19d98a7a132274b69c1304d60b4',
    },
    {
      // ynETH MAX — buffer-based instant redeem (ERC-4626), no
      // NFT-enumerable request surface.
      lst: '0x657d9aba1dbb59e53f9f3ecaa878447dcfc96dcb',
      brand: 'YieldNest',
      symbol: 'ynETHx',
      reader: 'noQueue',
    },
    {
      lst: '0xa35b1b31ce002fbf2058d22f30f95d405200a15b',
      brand: 'Stader',
      symbol: 'ETHx',
      reader: 'staderEthxQueue',
      withdrawalContract: '0x9f0491b32dbce587c50c4c43ab303b06478193a7',
    },
    {
      lst: '0xf03a7eb46d01d9ecaa104558c732cf82f6b6b645',
      brand: 'Stader',
      symbol: 'MaticX',
      reader: 'staderMaticXQueue',
      withdrawalContract: '0xf03a7eb46d01d9ecaa104558c732cf82f6b6b645',
      // Polygon IStakeManager on Ethereum (the contract used to
      // checkpoint Polygon validators from L1).
      polygonStakeManager: '0x5e3ef299fddf15eaa0432e6e66473ace8c13d908',
    },
    {
      lst: '0xbf5495efe5db9ce00f80364c8b423567e58d2110',
      brand: 'Renzo',
      symbol: 'ezETH',
      reader: 'renzoQueue',
      withdrawalContract: '0x5efc9d10e42fb517456f4ac41eb5e2ebe42c8918',
    },
    {
      lst: '0xa1290d69c65a6fe4df752f95823fae25cb99e5a7',
      brand: 'Kelp',
      symbol: 'rsETH',
      reader: 'kelpQueue',
      // LRTWithdrawalManager proxy (verified unchanged post-restart).
      // See INDEXING_STRATEGIES.md for the storage-slot derivation.
      withdrawalContract: '0x62de59c08eb5dae4b7e6f7a8cad3006d6965ec16',
    },
    // Caller-supplied IDs protocols on chain 1 — see
    // INDEXING_STRATEGIES.md. The orchestrator's `options.knownIds`
    // map must include `[lst]: bigint[]` (or `(validator, nonce)[]`
    // for TruFin) for the reader to return non-empty.
    {
      lst: '0xd5f7838f5c461feff7fe49ea5ebaf7728bb0adfa',
      brand: 'Mantle',
      symbol: 'mETH',
      reader: 'mantleCallerSuppliedIds',
      withdrawalContract: '0x38fdf7b489316e03ed8754ad339cb5c4483fdcf9',
    },
    {
      lst: '0xd9a442856c234a39a81a089c06451ebaa4306a72',
      brand: 'Puffer',
      symbol: 'pufETH',
      reader: 'pufferCallerSuppliedIds',
      withdrawalContract: '0xdda0483184e75a5579ef9635ed14baccf9d50283',
    },
    {
      lst: '0xf1c9acdc66974dfb6decb12aa385b9cd01190e38',
      brand: 'StakeWise',
      symbol: 'osETH',
      reader: 'stakeWiseSubgraph',
      // No single withdrawal contract — exits are per-vault. The
      // subgraph reader populates the per-request `lst` field with
      // the source vault address.
    },
    // No queue / off-chain protocols on chain 1.
    {
      lst: '0xae78736cd615f374d3085123a210448e74fc6393',
      brand: 'Rocket Pool',
      symbol: 'rETH',
      reader: 'noQueue',
    },
    {
      // Frax sfrxETH — vanilla ERC-4626 instant redeem to frxETH (the
      // frxETH → ETH leg is DEX-only, not a protocol queue).
      lst: '0xac3e018457b222d93114458476f3e3416abbe38f',
      brand: 'Frax',
      symbol: 'sfrxETH',
      reader: 'noQueue',
    },
    {
      lst: '0xbe9895146f7af43049ca1c1ae358b0541ea49704',
      brand: 'Coinbase',
      symbol: 'cbETH',
      reader: 'noQueue',
    },
    {
      lst: '0xfae103dc9cf190ed75350761e95403b7b8afa6c0',
      brand: 'Swell',
      symbol: 'rswETH',
      reader: 'swellNft',
      // rswEXIT — ERC721Enumerable (verified via
      // supportsInterface(0x780e9d63)). See INDEXING_STRATEGIES.md.
      withdrawalContract: '0x58749c46ffe97e4d79508a2c781c440f4756f064',
    },
    {
      // TruFin TruStakeMATICv2 — ERC-4626 vault, withdrawals routed
      // through Polygon validator unbond. Caller supplies
      // `(validator, nonce)` pairs captured at withdraw time; the
      // reader resolves details + claimability via `withdrawals(...)`
      // and `isClaimable(...)`.
      lst: '0xa43a7c62d56df036c187e1966c03e2799d8987ed',
      brand: 'TruFin',
      symbol: 'TruMATIC',
      reader: 'trufinCallerSuppliedIds',
      withdrawalContract: '0xa43a7c62d56df036c187e1966c03e2799d8987ed',
    },
    {
      // TruFin TruStake POL Vault — same validator-unbond withdrawal
      // mechanism as TruStakeMATICv2 (identical `withdrawals(...)` /
      // `isClaimable(...)` interface, verified on-chain). Caller
      // supplies `(validator, nonce)` pairs captured at withdraw time.
      lst: '0xc10214cde5d6754ec1e2220362f2120142c8e5e8',
      brand: 'TruFin',
      symbol: 'TruPOL',
      reader: 'trufinCallerSuppliedIds',
      withdrawalContract: '0xc10214cde5d6754ec1e2220362f2120142c8e5e8',
    },
  ],
  '56': [
    {
      // wBETH — Binance's UnwrapTokenV1 queue, the SAME contract address
      // on Ethereum and BNB. Entered via `wBETH.requestWithdrawEth`;
      // `getUserWithdrawRequests(user)` enumerates open requests, and
      // `claimWithdraw(index)` takes the user's ARRAY POSITION (swap-and-pop,
      // so ids shift on every claim — never cache them). `lockTime()` is
      // 10 days today but is admin-settable down to 2, and the ETH owed is
      // frozen at request time, so the position stops earning meanwhile.
      lst: '0xa2e3356610840701bdf5611a53974510ae27e2e1',
      brand: 'Binance',
      symbol: 'wBETH',
      reader: 'binanceWbethQueue',
      withdrawalContract: '0x79973d557cd9dd87eb61e250cc2572c990e20196',
    },
    // Ankr ankrBNB — Ankr unstake queue; reader not yet implemented.
    {
      lst: '0x52f24a5e03aee338da5fd9df68d2b6fae1178827',
      brand: 'Ankr',
      symbol: 'ankrBNB',
      reader: 'unverified',
    },
    {
      // slisBNB — ListaStakeManager withdrawal queue. Per-user
      // requests via getUserWithdrawalRequests; claimability + BNB
      // owed via getUserRequestStatus(user, idx). BNB validator unbond
      // (~7–15 days), no deterministic readyAt.
      lst: '0xb0b84d294e0c75a6abe60171b70edeb2efd14a1b',
      brand: 'Lista',
      symbol: 'slisBNB',
      reader: 'listaQueue',
      withdrawalContract: '0x1adb950d8bb3da4be104211d5ab038628e477fe6',
    },
    {
      // ynBNB — ERC-4626 redeem to slisBNB is synchronous; no
      // vault-level request queue to enumerate.
      lst: '0x304b5845b9114182ecb4495be4c91a273b74b509',
      brand: 'YieldNest',
      symbol: 'ynBNB',
      reader: 'noQueue',
    },
    {
      // ynBNB MAX — buffer-based instant redeem (ERC-4626).
      lst: '0x32c830f5c34122c6afb8ae87aba541b7900a2c5f',
      brand: 'YieldNest',
      symbol: 'ynBNBx',
      reader: 'noQueue',
    },
  ],
  '137': [
    // Ankr ankrMATIC — bridged, non-mintable on Polygon; exit via DEX only.
    {
      lst: '0x0e9b89007eee9c958c0eda24ef70723c2c93dd58',
      brand: 'Ankr',
      symbol: 'ankrMATIC',
      reader: 'noQueue',
    },
    {
      // Polygon-side MaticX is a bridged ERC-20 — users bridge to
      // Ethereum and claim from the L1 home contract.
      lst: '0xfa68fb4628dff1028cfec22b4162fccd0d45efb6',
      brand: 'Stader',
      symbol: 'MaticX',
      reader: 'noQueue',
    },
  ],
  '43114': [
    {
      // Treehouse tAVAX — same stack as tETH; 17-day wait here.
      lst: '0x14a84f1a61ccd7d1be596a6cc11fe33a36bc1646',
      brand: 'Treehouse',
      symbol: 'tAVAX',
      reader: 'treehouseRedemptionQueue',
      withdrawalContract: '0x765f6dc8496ca7ef1e4a391be10185229aacf04b',
    },
    // Ankr ankrAVAX — Ankr unstake queue; reader not yet implemented.
    {
      lst: '0xc3344870d52688874b06d844e0c36cc39fc727f6',
      brand: 'Ankr',
      symbol: 'ankrAVAX',
      reader: 'unverified',
    },
    {
      lst: '0x2b2c81e08f1af8835a78bb2a90ae924ace0ea4be',
      brand: 'BENQI',
      symbol: 'sAVAX',
      reader: 'benqiSavaxQueue',
      withdrawalContract: '0x2b2c81e08f1af8835a78bb2a90ae924ace0ea4be',
    },
    {
      // Avant savUSD — StakedUSDeV2-style cooldown. Verified on-chain:
      // cooldownDuration()=86400, cooldowns(address) ticket getter.
      lst: '0x06d47f3fb376649c3a9dafe069b3d6e35572219e',
      brand: 'Avant',
      symbol: 'savUSD',
      reader: 'ethenaCooldown',
    },
    {
      // Avant savBTC — same StakedUSDeV2 cooldown surface (verified on-chain).
      lst: '0x649342c6bff544d82df1b2ba3c93e0c22cdeba84',
      brand: 'Avant',
      symbol: 'savBTC',
      reader: 'ethenaCooldown',
    },
  ],
  // Monad LSTs — all four are `queued` in the main registry but their
  // per-protocol request enumeration is not implemented yet; listed as
  // `unverified` placeholders so the orchestrator surfaces the asset
  // (returning []) instead of silently omitting it.
  '143': [
    {
      lst: '0x1b68626dca36c7fe922fd2d55e4f631d962de19c',
      brand: 'Shmonad',
      symbol: 'shMON',
      reader: 'unverified',
    },
    {
      lst: '0x0c65a0bc65a5d819235b71f554d210d3f80e0852',
      brand: 'aPriori',
      symbol: 'aprMON',
      reader: 'unverified',
    },
    {
      lst: '0xa3227c5969757783154c60bf0bc1944180ed81b9',
      brand: 'Kintsu',
      symbol: 'sMON',
      reader: 'unverified',
    },
    {
      lst: '0x8498312a6b3cbd158bf0c93abdcf29e6e4f55081',
      brand: 'Magma',
      symbol: 'gMON',
      reader: 'unverified',
    },
  ],
  '146': [
    {
      lst: '0xe5da20f15420ad15de0fa650600afc998bbe3955',
      brand: 'Beets',
      symbol: 'stS',
      reader: 'beetsStSQueue',
      withdrawalContract: '0xe5da20f15420ad15de0fa650600afc998bbe3955',
    },
  ],
  '999': [
    {
      // Treehouse tHYPE — same stack as tETH; 10-day wait here.
      lst: '0xb83d27f0f4133a7b0377a88089583131a903fdc7',
      brand: 'Treehouse',
      symbol: 'tHYPE',
      reader: 'treehouseRedemptionQueue',
      withdrawalContract: '0xcebbb011c2f29b0ed25895c54662bd1ccad30280',
    },
    {
      // Hyperbeat hbHYPE Ultra — ERC-7540 async-redeem.
      lst: '0x96c6cbb6251ee1c257b2162ca0f39aa5fa44b1fb',
      brand: 'Hyperbeat',
      symbol: 'hbHYPE',
      reader: 'erc7540',
    },
    {
      // kHYPE — Kinetiq StakingManager Diamond. Per-user ids via
      // nextWithdrawalId + withdrawalRequests(user, id).
      lst: '0xfd739d4e423301ce9385c1fb8850539d657c296d',
      brand: 'Kinetiq',
      symbol: 'kHYPE',
      reader: 'kinetiqQueue',
      withdrawalContract: '0x393d0b87ed38fc779fd9611144ae649ba6082109',
    },
    {
      // beHYPE — Hyperbeat WithdrawManager.
      // getUserUnclaimedWithdrawals + withdrawalQueue + canClaimWithdrawal.
      lst: '0xd8fc8f0b03eba61f64d08b0bef69d80916e5dda9',
      brand: 'Hyperbeat',
      symbol: 'beHYPE',
      reader: 'beHypeQueue',
      withdrawalContract: '0x9d0b0877b9f2204cf414ca7862e4f03506822538',
    },
    {
      // wstHYPE — Valantis Overseer. getBurns(user) one-shot.
      lst: '0x94e8396e0869c9f2200760af0621afd240e1cf38',
      brand: 'Valantis',
      symbol: 'wstHYPE',
      reader: 'valantisBurnQueue',
      withdrawalContract: '0xb96f07367e69e86d6e9c3f29215885104813eeae',
    },
    {
      // stHYPE — same Valantis Overseer burn queue as wstHYPE.
      lst: '0xffaa4a3d97fe9107cef8a3f48c069f577ff76cc1',
      brand: 'Valantis',
      symbol: 'stHYPE',
      reader: 'valantisBurnQueue',
      withdrawalContract: '0xb96f07367e69e86d6e9c3f29215885104813eeae',
    },
  ],
  '42220': [
    {
      // stCELO (StakedCelo). Pending withdrawals enumerated on the
      // Account contract via `getPendingWithdrawals(beneficiary)`.
      lst: '0xc668583dcbdc9ae6fa3ce46462758188adfdfc24',
      brand: 'StakedCelo',
      symbol: 'stCELO',
      reader: 'stceloAccountQueue',
      withdrawalContract: '0x4aad04d41fd7fd495503731c5a2579e19054c432',
    },
  ],
  '8217': [
    {
      // Lair Finance stKAIA — 7-day unbonding queue on the stKAIA
      // contract itself (`unstake(node, amount)` → `claim(requestId)`).
      // No confirmed public getter to enumerate pending requests, so the
      // reader takes caller-supplied `requestId`s captured from the
      // unstake receipt. See INDEXING_STRATEGIES.md.
      lst: '0x42952b873ed6f7f0a7e4992e2a9818e3a9001995',
      brand: 'Lair Finance',
      symbol: 'stKAIA',
      reader: 'lairCallerSuppliedIds',
      withdrawalContract: '0x42952b873ed6f7f0a7e4992e2a9818e3a9001995',
    },
  ],
  '80094': [
    {
      // iBERA — WithdraworLite global queue. No per-user getter; the
      // reader scans `requests(requestId)` (keyed by a global nonce) and
      // filters by the `receiver` field. Claimability is frontier-based
      // via `requestsFinalisedUntil()`.
      lst: '0x9b6761bf2397bb5a6624a856cc84a3a14dcd3fe5',
      brand: 'Infrared',
      symbol: 'iBERA',
      reader: 'iberaQueue',
      withdrawalContract: '0x8c0e122960dc2e97dc0059c07d6901dce72818e1',
    },
    {
      // LBGT — BeraPawForge. Per-user `redeemPosition(account)` →
      // (queued, available) LBGT amounts (1:1 BERA).
      lst: '0xbaadcc2962417c01af99fb2b7c75706b9bd6babe',
      brand: 'BeraPaw',
      symbol: 'LBGT',
      reader: 'berapawRedeemQueue',
      withdrawalContract: '0xfeedb9750d6ac77d2e52e0c9eb8fb79f9de5cafe',
    },
    {
      // yBGT — Bearn. `redeem(amount)` exits instantly to native BERA;
      // nothing to enumerate.
      lst: '0x7e768f47dfdd5dae874aac233f1bc5817137e453',
      brand: 'Bearn',
      symbol: 'yBGT',
      reader: 'noQueue',
    },
    {
      // iBGT — Infrared. No on-chain redeem to BGT (BGT non-transferable);
      // exit is DEX-only.
      lst: '0xac03caba51e17c86c921e1f6cbfbdc91f8bb2e6b',
      brand: 'Infrared',
      symbol: 'iBGT',
      reader: 'noQueue',
    },
    {
      // beraETH — Dinero. Redemption is a cross-chain (LayerZero) queue on
      // the rberaETH base LST; no simple per-user enumeration confirmed.
      lst: '0x6fc6545d5cde268d5c7f1e476d444f39c995120d',
      brand: 'Dinero',
      symbol: 'beraETH',
      reader: 'unverified',
    },
    {
      // stBGT — Stride/BakerDAO. On-chain redeem contract not recovered.
      lst: '0x2cec7f1ac87f5345ced3d6c74bbb61bfae231ffb',
      brand: 'Stride',
      symbol: 'stBGT',
      reader: 'unverified',
    },
  ],
  '50': [
    {
      // psXDC (PrimeStaking V3 vault). `redeemWithQueue` appends to a
      // GLOBAL FIFO queue keyed by requestId; no per-user getter, so the
      // reader scans `withdrawalRequests(id)` and filters by `receiver`.
      // Claimability comes from the per-request `processed` flag
      // (equivalently `id < nextUnprocessedWithdrawalRequestId()`); funded
      // requests are pulled via `claimQueuedAssets(receiver)`. The vault is
      // its own withdrawal contract.
      lst: '0x98d916f5773ac0482b49856f2659d6c32114c4ba',
      brand: 'PrimeStaking',
      symbol: 'psXDC',
      reader: 'primeStakingQueue',
      withdrawalContract: '0x98d916f5773ac0482b49856f2659d6c32114c4ba',
    },
  ],
  '1116': [
    // Core DAO LSTs — CoreDAO stakers unbond via the Earn / staking
    // contracts; per-protocol queue readers not yet implemented.
    {
      lst: '0xb3a8f0f0da9ffc65318aa39e55079796093029ad',
      brand: 'Core',
      symbol: 'stCORE',
      reader: 'unverified',
    },
    {
      lst: '0xc5555ea27e63cd89f8b227dece2a3916800c0f4f',
      brand: 'b14g',
      symbol: 'dualCORE',
      reader: 'unverified',
    },
    {
      lst: '0xa20b3b97df3a02f9185175760300a06b4e0a2c05',
      brand: 'StakedCore',
      symbol: 'SCORE',
      reader: 'unverified',
    },
    // pumpBTC on Core — bridged, non-mintable; exit via DEX only.
    {
      lst: '0x5a2aa871954ebdf89b1547e75d032598356caad5',
      brand: 'PumpBTC',
      symbol: 'pumpBTC',
      reader: 'noQueue',
    },
  ],
  // Flare (14) — Firelight stXRP. Period-based queue: `withdraw`/`redeem`
  // create a `WithdrawRequest` for `currentPeriod() + 1`; `claimWithdraw(period)`
  // pays out after the period ends. The surface (`withdrawSharesOf(period, account)`,
  // `withdrawalsOf(period, account)`, `isWithdrawClaimed(period, account)`) is
  // verified on-chain, but the period-window enumeration reader is not yet
  // built — placeholder so the orchestrator surfaces the asset (returns []).
  '14': [
    {
      lst: '0x4c18ff3c89632c3dd62e796c0afa5c07c4c1b2b3',
      brand: 'Firelight',
      symbol: 'stXRP',
      reader: 'unverified',
    },
    {
      // SparkDEX stFLR — `requestWithdrawal()` / `claimWithdrawal()` queue +
      // `instantWithdrawal()` fee leg. Reader not built yet; placeholder so
      // the orchestrator surfaces the asset.
      lst: '0x0988c6ba244a90c07a917ebe609eb3264be716ff',
      brand: 'SparkDEX',
      symbol: 'stFLR',
      reader: 'unverified',
    },
    {
      // Sceptre sFLR — unlock-request queue (`cooldownPeriod()`) +
      // `instantRedeem()` fee leg. Reader not built yet; placeholder so the
      // orchestrator surfaces the asset.
      lst: '0x12e605bc104e93b45e1ad99f9e555f659051c2bb',
      brand: 'Sceptre',
      symbol: 'sFLR',
      reader: 'unverified',
    },
  ],
}

/** Returns the withdrawal-registry entries for a chain, or `[]`. */
export const getLstWithdrawalRegistry = (
  chainId: string,
  extraEntries?: LstWithdrawalRegistryEntry[],
): LstWithdrawalRegistryEntry[] => {
  const base = LST_WITHDRAWAL_REGISTRY[chainId] ?? []
  if (!extraEntries || extraEntries.length === 0) return base
  // De-dup by lowercased `lst` — a caller-supplied entry for an address
  // already in the static registry is ignored (static wins).
  const seen = new Set(base.map((e) => e.lst.toLowerCase()))
  const extra = extraEntries.filter((e) => !seen.has(e.lst.toLowerCase()))
  return extra.length === 0 ? base : [...base, ...extra]
}
